import { memo } from 'react';
import type { ExecutionDetailCardProps } from './ExecutionDetailCard.types';
import { getWidgetStores } from '@/entities/widget';
import { MotionBox } from '@/shared/ui/motion';
import { AsyncImage } from '../AsyncImage';
import { useTranslation } from 'react-i18next';

// Platform icon components
const TikTokIcon = memo(function TikTokIcon() {
  return (
    <svg viewBox="0 0 16 16" className="size-4 shrink-0 fill-current" aria-hidden="true">
      <path d="M13.5 5.5V8C11.5 8 10 7 9.5 5.5V11C9.5 13.2 7.7 15 5.5 15C3.3 15 1.5 13.2 1.5 11C1.5 8.8 3.3 7 5.5 7V9.5C4.7 9.5 4 10.2 4 11C4 11.8 4.7 12.5 5.5 12.5C6.3 12.5 7 11.8 7 11V1H9.5C9.5 3.2 11.3 5 13.5 5V5.5Z" />
    </svg>
  );
});

const InstagramIcon = memo(function InstagramIcon() {
  return (
    <svg viewBox="0 0 16 16" className="size-4 shrink-0 fill-current" aria-hidden="true">
      <path d="M8 1.44C10.136 1.44 10.39 1.449 11.233 1.488C12.013 1.524 12.437 1.653 12.719 1.76C13.093 1.903 13.359 2.072 13.636 2.35C13.914 2.628 14.083 2.893 14.226 3.267C14.333 3.549 14.462 3.973 14.498 4.753C14.537 5.596 14.546 5.85 14.546 7.986C14.546 10.122 14.537 10.376 14.498 11.219C14.462 11.999 14.333 12.423 14.226 12.705C14.083 13.079 13.914 13.345 13.636 13.622C13.358 13.9 13.093 14.069 12.719 14.212C12.437 14.319 12.013 14.448 11.233 14.484C10.39 14.523 10.136 14.532 8 14.532C5.864 14.532 5.61 14.523 4.767 14.484C3.987 14.448 3.563 14.319 3.281 14.212C2.907 14.069 2.641 13.9 2.364 13.622C2.086 13.344 1.917 13.079 1.774 12.705C1.667 12.423 1.538 11.999 1.502 11.219C1.463 10.376 1.454 10.122 1.454 7.986C1.454 5.85 1.463 5.596 1.502 4.753C1.538 3.973 1.667 3.549 1.774 3.267C1.917 2.893 2.086 2.627 2.364 2.35C2.642 2.072 2.907 1.903 3.281 1.76C3.563 1.653 3.987 1.524 4.767 1.488C5.61 1.449 5.864 1.44 8 1.44ZM8 0C5.827 0 5.555 0.01 4.702 0.049C3.851 0.088 3.269 0.222 2.76 0.42C2.234 0.624 1.787 0.898 1.343 1.343C0.898 1.787 0.624 2.234 0.42 2.76C0.222 3.269 0.088 3.851 0.049 4.702C0.01 5.555 0 5.827 0 8C0 10.173 0.01 10.445 0.049 11.298C0.088 12.149 0.222 12.731 0.42 13.24C0.624 13.766 0.898 14.213 1.343 14.657C1.787 15.102 2.234 15.376 2.76 15.58C3.269 15.778 3.851 15.912 4.702 15.951C5.555 15.99 5.827 16 8 16C10.173 16 10.445 15.99 11.298 15.951C12.149 15.912 12.731 15.778 13.24 15.58C13.766 15.376 14.213 15.102 14.657 14.657C15.102 14.213 15.376 13.766 15.58 13.24C15.778 12.731 15.912 12.149 15.951 11.298C15.99 10.445 16 10.173 16 8C16 5.827 15.99 5.555 15.951 4.702C15.912 3.851 15.778 3.269 15.58 2.76C15.376 2.234 15.102 1.787 14.657 1.343C14.213 0.898 13.766 0.624 13.24 0.42C12.731 0.222 12.149 0.088 11.298 0.049C10.445 0.01 10.173 0 8 0Z" />
      <path d="M8 3.892C5.734 3.892 3.892 5.734 3.892 8C3.892 10.266 5.734 12.108 8 12.108C10.266 12.108 12.108 10.266 12.108 8C12.108 5.734 10.266 3.892 8 3.892ZM8 10.667C6.527 10.667 5.333 9.473 5.333 8C5.333 6.527 6.527 5.333 8 5.333C9.473 5.333 10.667 6.527 10.667 8C10.667 9.473 9.473 10.667 8 10.667Z" />
      <path d="M13.2307 3.72903C13.2307 4.25903 12.8007 4.68903 12.2707 4.68903C11.7407 4.68903 11.3107 4.25903 11.3107 3.72903C11.3107 3.19903 11.7407 2.76903 12.2707 2.76903C12.8007 2.76903 13.2307 3.19903 13.2307 3.72903Z" />
    </svg>
  );
});

/**
 * Helper to get platform icon
 * Returns the appropriate icon component based on platform name
 */
const getPlatformIcon = (platform: string): React.ReactNode => {
  const lowerPlatform = platform.toLowerCase();
  if (lowerPlatform.includes('tiktok')) {
    return <TikTokIcon />;
  }
  if (lowerPlatform.includes('instagram') || lowerPlatform.includes('reel')) {
    return <InstagramIcon />;
  }
  return null;
};

interface PlatformChipProps {
  platform: string;
}

/**
 * PlatformChip - Displays a platform with its icon
 * Memoized to prevent unnecessary re-renders
 */
const PlatformChip = memo(function PlatformChip({ platform }: PlatformChipProps) {
  const icon = getPlatformIcon(platform);

  return (
    <span className="flex items-center gap-1 text-foreground">
      {icon}
      <span className="text-body-lg">{platform}</span>
    </span>
  );
});

/**
 * ExecutionDetailCard - Displays detailed information about an idea execution
 *
 * Features:
 * - Responsive layout (horizontal in fullscreen, vertical in panel mode)
 * - Uses AsyncImage for consistent image loading
 * - Shows platform icons for social media platforms
 * - Accessible with proper semantic structure
 * - Memoized to prevent unnecessary re-renders in lists
 */
export const ExecutionDetailCard = memo(function ExecutionDetailCard({
  execution,
  index,
}: ExecutionDetailCardProps) {
  const { t } = useTranslation('contentTest');

  const { sidePanelLayoutMode } = getWidgetStores().uiStore.useSidePanelLayoutMode();
  const isFullscreen = sidePanelLayoutMode === 'fullscreen';

  // Parse platform - could be "TikTok, Instagram reel" or just "Instagram"
  const platforms = execution.platform
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);

  const executionId = `execution-${index}`;

  const imageAltText = t('ideationWidget.executionDetailCard.visualForExecution', {
    title: execution.title,
  });

  return (
    <MotionBox
      layout="preserve-aspect"
      component="article"
      aria-labelledby={`${executionId}-title`}
      className="flex flex-col"
    >
      {/* Section Header */}
      <div className="flex w-full flex-col items-center justify-between border-t border-border-light pt-4 pb-10">
        <h3
          id={`${executionId}-title`}
          className="w-full text-heading-md font-medium text-foreground"
        >
          {t('ideationWidget.executionDetailCard.executionNumber', {
            executionNumber: index + 1,
          })}
        </h3>
      </div>

      {/* Card Content */}
      <MotionBox
        layout
        className={`mx-6 flex gap-12 rounded-3xl border border-border-light p-10 ${
          isFullscreen ? 'flex-row' : 'flex-col'
        }`}
      >
        {/* Left: Copy section */}
        <div className="flex flex-1 flex-col justify-center gap-4">
          {/* Headline */}
          <div className="flex flex-col gap-4">
            <h4 className="max-w-135 text-heading-xl text-muted-foreground">{execution.title}</h4>
            <p className="text-body-lg text-foreground">{execution.description}</p>
          </div>

          {/* Best fit platform */}
          <div className="flex flex-col gap-2">
            <h5 className="text-body-lg font-medium text-muted-foreground">
              {t('ideationWidget.executionDetailCard.bestFitPlatform')}
            </h5>
            <ul
              className="m-0 flex list-none flex-wrap items-center gap-2.5 p-0"
              aria-label={t('ideationWidget.executionDetailCard.recommendedPlatforms')}
            >
              {platforms.map((platform, idx) => (
                <li key={idx}>
                  <PlatformChip platform={platform} />
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right: Image */}
        <MotionBox
          layout
          className={`shrink-0 overflow-hidden rounded-3xl bg-muted ${
            isFullscreen ? 'aspect-[290/163] h-81.5' : 'aspect-[651/367] h-91.75'
          }`}
        >
          <AsyncImage
            assetId={execution.imageAssetId}
            alt={imageAltText}
            variant="light"
            placeholderText={t('ideationWidget.executionDetailCard.noImageAvailable')}
          />
        </MotionBox>
      </MotionBox>
    </MotionBox>
  );
});
